import { Account, Client, Functions, Models } from 'appwrite';
import { APPWRITE_API, GITHUB_API_BASE_URL, base64ToArrayBuffer, findNodeByPath } from '@lved/shared-core';
import type { FSNode, SuccessFlagResponse } from '@lved/shared-core';
import type {
    CommitDetailsResult,
    CommitFilePayload,
    CommitResult,
    GitBlobResult,
    GitCommitResult,
    GitRefResult,
    GitTreeCreateResult,
    GitTreeItem,
    GitHubTreeItem,
    JsonResult,
    QueryParams,
    TokenResult,
    TreeResult
} from './githubTypes.js';

declare global {
    interface Window {
        LVED_GITHUB_ARCHIVE_PROXY_URL?: string;
    }
}

const isSuccessFlagResponse = (value: unknown): value is SuccessFlagResponse =>
    typeof value === 'object' && value !== null && 'success' in value && typeof value.success === 'boolean';

/**
 * Service for making GitHub API calls through AppWrite Functions
 *
 * Note: This service is only used for fetching repository files.
 * OAuth authentication is handled entirely client-side by the AppWrite SDK.
 */
export class AppwriteGitHubService {
    private static APPWRITE_FUNCTION_ID: string = '689a38910029cb5cb948';
    private static API_URL: string = GITHUB_API_BASE_URL;

    private static functions: Functions | null;
    private static account: Account | null;
    private static githubToken: string | null = null;
    private static archiveProxyBaseUrl: string = '';

    constructor(client: Client) {
        AppwriteGitHubService.functions = new Functions(client);
        AppwriteGitHubService.account = new Account(client);
    }

    /**
     * Load the GitHub auth token from Appwrite; must run before other requests.
     */
    public static async getGithubToken(): Promise<void> {
        if (!this.functions) throw new Error('AppwriteGitHubService has not been initialized.');

        const tokenResponse: Models.Execution = await this.functions.createExecution(
            AppwriteGitHubService.APPWRITE_FUNCTION_ID,
            JSON.stringify({ endpoint: '/my-token' })
        );

        const tokenResult: TokenResult = JSON.parse(tokenResponse.responseBody) as TokenResult;

        if (!tokenResult.success) throw new Error(tokenResult.error);
        if (!tokenResult.githubToken) throw new Error('GitHub token response did not include a token.');

        AppwriteGitHubService.githubToken = tokenResult.githubToken;
    }

    /**
     * Base GitHub API GET wrapper with auth, params, and basic error handling.
     */
    private static async fetchBase(
        endpoint: string,
        params?: QueryParams,
        accept: string = 'application/vnd.github.v3+json'
    ): Promise<Response> {
        if (!AppwriteGitHubService.functions) throw new Error('AppwriteGitHubService has not been initialized.');
        if (!AppwriteGitHubService.githubToken) throw new Error('GitHub token has not been loaded.');

        const url: URL = new URL(AppwriteGitHubService.API_URL + endpoint);

        // Add query parameters if provided
        if (params) Object.keys(params).forEach((key) => url.searchParams.append(key, String(params[key])));

        const response: Response = await fetch(url, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${AppwriteGitHubService.githubToken}`,
                Accept: accept,
                'User-Agent': 'LVGL-Pro-Online'
            }
        });

        if (!response.ok) {
            if (response.status === 403) throw new Error('GitHub API rate limit exceeded or insufficient permissions');

            if (response.status === 404) throw new Error('Repository or resource not found');
        }

        return response;
    }

    /**
     * Fetch JSON from GitHub API using the authenticated base request.
     */
    private static async fetch(endpoint: string, params?: QueryParams): Promise<unknown> {
        const response: Response = await AppwriteGitHubService.fetchBase(endpoint, params);
        const jsonResult: unknown = await response.json();

        if (jsonResult === null || jsonResult === undefined) throw new Error('Request returned empty response');

        return jsonResult;
    }

    /**
     * Fetch raw (non-JSON) content from GitHub API.
     */
    private static async fetchRaw(endpoint: string, params?: QueryParams): Promise<ArrayBuffer> {
        const response: Response = await AppwriteGitHubService.fetchBase(
            endpoint,
            params,
            'application/vnd.github.v3.raw+json'
        );
        const bufferResponse: ArrayBuffer = await response.arrayBuffer();

        if (!bufferResponse) throw new Error('Request returned empty response');

        return bufferResponse;
    }

    /**
     * Fetch a complete repository ZIP archive through the Appwrite archive proxy.
     *
     * Public repositories are downloaded anonymously through the proxy to avoid
     * GitHub archive/CORS redirect issues in the browser.
     *
     * Private repositories additionally require:
     * - an authenticated Appwrite user session
     * - a valid Appwrite JWT
     * - a stored GitHub OAuth access token on the server side
     */
    static async fetchRepositoryArchive(
        owner: string,
        repo: string,
        ref: string,
        privateRepository: boolean = false
    ): Promise<Uint8Array> {
        const proxyBaseUrl: string = AppwriteGitHubService.getArchiveProxyBaseUrl();
        if (!proxyBaseUrl) throw new Error('GitHub archive proxy URL is not configured.');

        const baseUrl: string = proxyBaseUrl.endsWith('/') ? proxyBaseUrl : `${proxyBaseUrl}/`;
        const url: URL = new URL('github/archive.zip', baseUrl);
        url.searchParams.set('owner', owner);
        url.searchParams.set('repo', repo);
        url.searchParams.set('ref', ref);

        const headers: Record<string, string> = {
            Accept: 'application/zip'
        };

        if (privateRepository) {
            if (!AppwriteGitHubService.account) throw new Error('AppwriteGitHubService has not been initialized.');

            const jwtResult: Models.Jwt = await AppwriteGitHubService.account.createJWT();
            url.searchParams.set('private', 'true');
            headers.Authorization = `Bearer ${jwtResult.jwt}`;
        }

        const response: Response = await fetch(url, {
            method: 'GET',
            headers
        });

        if (!response.ok) {
            throw new Error(
                `Failed to fetch repository archive through Appwrite: ${response.status} ${response.statusText}`
            );
        }

        return new Uint8Array(await response.arrayBuffer());
    }

    static setArchiveProxyBaseUrl(url: string): void {
        AppwriteGitHubService.archiveProxyBaseUrl = url;
    }

    private static getArchiveProxyBaseUrl(): string {
        if (AppwriteGitHubService.archiveProxyBaseUrl) return AppwriteGitHubService.archiveProxyBaseUrl;
        if (typeof window !== 'undefined' && window.LVED_GITHUB_ARCHIVE_PROXY_URL) {
            return window.LVED_GITHUB_ARCHIVE_PROXY_URL;
        }
        return APPWRITE_API;
    }

    /**
     * Ask the Appwrite function if the repo is supported for this environment.
     */
    public static validateRepoSupport = async (origin: string, owner: string, repo: string): Promise<boolean> => {
        if (!this.functions) throw new Error('AppwriteGitHubService has not been initialized.');

        const validationResponse: Models.Execution = await this.functions.createExecution(
            AppwriteGitHubService.APPWRITE_FUNCTION_ID,
            JSON.stringify({ endpoint: '/validate-repo', params: { origin, owner, repo } })
        );
        const result: unknown = JSON.parse(validationResponse.responseBody);
        if (!isSuccessFlagResponse(result)) throw new Error('Repository validation returned an invalid response.');

        return result.success;
    };

    /**
     * Fetch repository contents from GitHub via Appwrite function
     *
     * This method calls the 'github-api-proxy' function deployed on Appwrite
     * to fetch repository contents. The function uses the user's GitHub token
     * stored in Appwrite user preferences to authenticate with GitHub API.
     */
    static async fetchRepositoryContents(
        owner: string,
        repo: string,
        path: string = '',
        branch: string = 'main'
    ): Promise<FSNode[]> {
        // Always use the Tree API for efficiency, then filter for specific paths
        const allNodes: FSNode[] = await this.fetchRepositoryTree(owner, repo, branch);

        // If no specific path is requested, return all root-level items
        if (!path) return allNodes;

        // Find the specific path in the tree
        const targetNode: FSNode | null = findNodeByPath(allNodes, path);
        if (!targetNode) {
            if (path && path !== 'ui' && !path.endsWith('/ui')) {
                console.warn(`Path '${path}' not found in repository tree`);
            }
            return [];
        }

        // Return the children of the target path, or the node itself if it is a file
        return targetNode.type === 'directory' && targetNode.children ? targetNode.children : [targetNode];
    }

    /**
     * Fetch entire repository tree using GitHub's Tree API via Appwrite function
     * This is the most efficient method as it gets all files in one API call
     */
    static async fetchRepositoryTree(owner: string, repo: string, branch: string = 'main'): Promise<FSNode[]> {
        try {
            // Get the commit SHA for the branch
            const commitResult: CommitResult = (await AppwriteGitHubService.fetch(
                `/repos/${owner}/${repo}/branches/${branch}`
            )) as CommitResult;

            const commitSha: string = commitResult.commit.sha;

            // Get the tree SHA from the commit
            const commitDetailsResult: CommitDetailsResult = (await AppwriteGitHubService.fetch(
                `/repos/${owner}/${repo}/git/commits/${commitSha}`
            )) as CommitDetailsResult;

            const treeSha: string = commitDetailsResult.tree.sha;

            // Get the complete tree recursively
            const treeResult: TreeResult = (await AppwriteGitHubService.fetch(
                `/repos/${owner}/${repo}/git/trees/${treeSha}`,
                {
                    recursive: '1'
                }
            )) as TreeResult;

            return this.convertTreeToFSNodes(treeResult.tree);
        } catch (error: unknown) {
            console.error(`Error fetching repository tree: ${String(error)}`);
            if (error instanceof Error) {
                console.error(`Error details:`, {
                    message: error.message,
                    stack: error.stack
                });
            }
            throw error;
        }
    }

    /**
     * Check whether a branch exists in a repository.
     */
    static async branchExists(owner: string, repo: string, branch: string): Promise<boolean> {
        try {
            const response: Response = await this.fetchBase(`/repos/${owner}/${repo}/branches/${branch}`);
            return response.ok;
        } catch (error: unknown) {
            console.error(`Error checking branch "${branch}": ${String(error)}`);
            return false;
        }
    }

    static async getBranchSha(owner: string, repo: string, branch: string = 'main'): Promise<string> {
        const commitResult: CommitResult = (await AppwriteGitHubService.fetch(
            `/repos/${owner}/${repo}/branches/${branch}`
        )) as CommitResult;
        if (!commitResult?.commit?.sha) {
            throw new Error(`Branch response missing commit SHA for ${owner}/${repo}@${branch}`);
        }
        return commitResult.commit.sha;
    }

    /**
     * Convert GitHub tree data to FSNode format
     */
    private static convertTreeToFSNodes(treeItems: GitHubTreeItem[]): FSNode[] {
        const fsNodes: FSNode[] = [];
        const pathMap: Map<string, FSNode> = new Map<string, FSNode>();

        // Sort items by path to ensure directories come before their contents
        const sortedItems: GitHubTreeItem[] = treeItems.sort((a, b) => a.path.localeCompare(b.path));

        for (const item of sortedItems) {
            const pathParts: string[] = item.path.split('/');
            const name: string = pathParts[pathParts.length - 1];

            const fsNode: FSNode = {
                name,
                path: item.path,
                type: item.type === 'tree' ? 'directory' : 'file',
                size: item.size || 0,
                children: item.type === 'tree' ? [] : undefined
            };

            // Store the node in the path map
            pathMap.set(item.path, fsNode);

            // If this is a root-level item, add it to the result
            if (pathParts.length === 1) {
                fsNodes.push(fsNode);
            } else {
                // Add to parent directory
                const parentPath: string = pathParts.slice(0, -1).join('/');
                const parentNode: FSNode | undefined = pathMap.get(parentPath);
                if (parentNode?.children) parentNode.children.push(fsNode);
            }
        }

        return fsNodes;
    }

    /**
     * Fetch file content from GitHub via Appwrite function
     *
     * This method calls the 'github-api-proxy' function to fetch file contents.
     * The content is returned as a decoded string from base64.
     */
    static async fetchFileContent(owner: string, repo: string, path: string, branch: string = 'main'): Promise<string> {
        const response: Response = await this.fetchBase(`/repos/${owner}/${repo}/contents/${path}`, {
            ref: branch
        });
        const rawText: string = await response.text();
        const contentType: string = response.headers.get('content-type') ?? '';
        let jsonResult: JsonResult | null = null;

        if (contentType.includes('application/json')) {
            try {
                jsonResult = JSON.parse(rawText) as JsonResult;
            } catch (error: unknown) {
                console.warn(`Failed to parse JSON response for ${path}: ${String(error)}`);
            }
        }

        if (jsonResult?.content) {
            // Decode base64 content to UTF-8
            const decoder: TextDecoder = new TextDecoder('utf-8');
            return decoder.decode(base64ToArrayBuffer(jsonResult.content));
        }

        return rawText;
    }

    /**
     * Fetch binary file content from GitHub via Appwrite function
     *
     * This method calls the 'github-api-proxy' function to fetch binary file contents.
     * The content is returned as a Uint8Array from base64-decoded data.
     */
    static async fetchBinaryFileContent(
        owner: string,
        repo: string,
        path: string,
        branch: string = 'main'
    ): Promise<Uint8Array> {
        const result: ArrayBuffer = await this.fetchRaw(`/repos/${owner}/${repo}/contents/${path}`, { ref: branch });
        return new Uint8Array(result);
    }

    /**
     * Create manifest data using AppwriteGitHubService
     */
    static async createManifestFromRepo(
        owner: string,
        repo: string,
        basePath: string,
        branch: string = 'main'
    ): Promise<{
        path: string;
        children: FSNode[];
    }> {
        const children: FSNode[] = await this.fetchRepositoryContents(owner, repo, basePath, branch);

        return {
            path: basePath,
            children
        };
    }

    /**
     * Create/update/delete files by committing to a branch via GitHub's git API.
     *
     * Flow:
     * 1) Read the branch ref to get the latest commit SHA.
     * 2) Fetch the commit to get its base tree SHA.
     * 3) Create blobs for each file (or null SHAs for deletions).
     * 4) Create a new tree from the base tree plus changes.
     * 5) Create a new commit pointing to the new tree.
     * 6) Update the branch ref to the new commit.
     */
    static async commitFiles(params: {
        owner: string;
        repo: string;
        branch: string;
        message: string;
        files: CommitFilePayload[];
    }): Promise<void> {
        const { owner, repo, branch, message, files: commitableFiles } = params;

        const encodeBase64 = (text: string): string => {
            const bytes: Uint8Array = new TextEncoder().encode(text);
            let binary: string = '';
            bytes.forEach((byte: number) => (binary += String.fromCharCode(byte)));
            return btoa(binary);
        };

        const encodeBase64Bytes = (bytes: Uint8Array): string => {
            let binary: string = '';
            bytes.forEach((byte: number) => (binary += String.fromCharCode(byte)));
            return btoa(binary);
        };

        const headers: Record<string, string> = {
            Authorization: `Bearer ${AppwriteGitHubService.githubToken}`,
            Accept: 'application/vnd.github+json',
            'User-Agent': 'LVGL-Pro-Online'
        };

        // Get latest commit SHA for the branch
        const refResponse: Response = await fetch(
            `${AppwriteGitHubService.API_URL}/repos/${owner}/${repo}/git/refs/heads/${branch}`,
            {
                headers
            }
        );
        if (!refResponse.ok) {
            throw new Error(
                `Failed to read branch ref: ${refResponse.status} ${refResponse.statusText} - ${await refResponse.text()}`
            );
        }
        const refData: GitRefResult = await refResponse.json();
        const latestCommitSha: string = refData.object.sha;

        // Get base tree SHA
        const latestCommitResponse: Response = await fetch(
            `${AppwriteGitHubService.API_URL}/repos/${owner}/${repo}/git/commits/${latestCommitSha}`,
            {
                headers
            }
        );
        if (!latestCommitResponse.ok) {
            throw new Error(
                `Failed to read latest commit: ${latestCommitResponse.status} ${latestCommitResponse.statusText} - ${await latestCommitResponse.text()}`
            );
        }
        const latestCommit: GitCommitResult = await latestCommitResponse.json();
        const baseTreeSha: string = latestCommit.tree.sha;

        // Create blobs for each file to be committed
        const treeItems: GitTreeItem[] = [];
        for (const file of commitableFiles) {
            if (file.deleted) {
                treeItems.push({
                    path: file.path,
                    mode: '100644',
                    type: 'blob',
                    sha: null
                });
                continue;
            }

            if (file.content === undefined) throw new Error(`Missing content for ${file.path}`);

            const encodedContent: string =
                file.content instanceof Uint8Array ? encodeBase64Bytes(file.content) : encodeBase64(file.content);

            const blobResponse: Response = await fetch(
                `${AppwriteGitHubService.API_URL}/repos/${owner}/${repo}/git/blobs`,
                {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        content: encodedContent,
                        encoding: 'base64'
                    })
                }
            );
            if (!blobResponse.ok) {
                throw new Error(
                    `Failed to create blob for ${file.path}: ${blobResponse.status} ${blobResponse.statusText} - ${await blobResponse.text()}`
                );
            }
            const blobResult: GitBlobResult = await blobResponse.json();
            treeItems.push({
                path: file.path,
                mode: '100644',
                type: 'blob',
                sha: blobResult.sha
            });
        }

        // Create new tree
        const treeResponse: Response = await fetch(
            `${AppwriteGitHubService.API_URL}/repos/${owner}/${repo}/git/trees`,
            {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    base_tree: baseTreeSha,
                    tree: treeItems
                })
            }
        );
        if (!treeResponse.ok) {
            throw new Error(
                `Failed to create tree: ${treeResponse.status} ${treeResponse.statusText} - ${await treeResponse.text()}`
            );
        }
        const treeResult: GitTreeCreateResult = await treeResponse.json();

        // Create commit
        const commitResponse: Response = await fetch(
            `${AppwriteGitHubService.API_URL}/repos/${owner}/${repo}/git/commits`,
            {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    message,
                    tree: treeResult.sha,
                    parents: [latestCommitSha]
                })
            }
        );
        if (!commitResponse.ok) {
            throw new Error(
                `Failed to create commit: ${commitResponse.status} ${commitResponse.statusText} - ${await commitResponse.text()}`
            );
        }
        const commitResult: GitCommitResult = await commitResponse.json();

        // Update ref to point to new commit
        const updateRefResponse: Response = await fetch(
            `${AppwriteGitHubService.API_URL}/repos/${owner}/${repo}/git/refs/heads/${branch}`,
            {
                method: 'PATCH',
                headers,
                body: JSON.stringify({ sha: commitResult.sha, force: false })
            }
        );
        if (!updateRefResponse.ok) {
            throw new Error(
                `Failed to update branch ref: ${updateRefResponse.status} ${updateRefResponse.statusText} - ${await updateRefResponse.text()}`
            );
        }
    }
}
